import './TransferPrintModal.css';

import { type FormEvent, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Field } from '../../components/Field';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { fmtInt } from '../../lib/format';
import type { TransferView } from '../../lib/transfers';
import type { Product } from '../../types/domain';

const STATUS_TH: Record<TransferView['status'], string> = {
  draft: 'ร่าง',
  sent: 'ส่งแล้ว',
  received: 'รับแล้ว',
  cancelled: 'ยกเลิก',
};

const fmtDateTime = (value: number): string =>
  new Intl.DateTimeFormat('th-TH-u-nu-latn', {
    timeZone: 'Asia/Bangkok',
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(new Date(value));

interface TransferPrintFormState {
  fromContact: string;
  fromPhone: string;
  toContact: string;
  toPhone: string;
  courierName: string;
  courierPhone: string;
  pickupDate: string;
  pickupTime: string;
  vehicleOrChannel: string;
  note: string;
}

const TEXTAREA_CLASS =
  'min-h-20 resize-y rounded-btn border border-line bg-surface px-3 py-2 text-body text-ink placeholder:text-ink-4 transition-colors duration-100 hover:border-ink-3/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

function createInitialForm(transfer: TransferView): TransferPrintFormState {
  return {
    fromContact: transfer.createdByName ?? '',
    fromPhone: '',
    toContact: transfer.receivedByName ?? '',
    toPhone: '',
    courierName: '',
    courierPhone: '',
    pickupDate: '',
    pickupTime: '',
    vehicleOrChannel: '',
    note: '',
  };
}

function printDate(value: string): string {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}

function DocumentField({
  label,
  value,
  className = '',
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-[9px] font-semibold text-slate-500">{label}</div>
      <div className="min-h-[20px] border-b border-slate-400 pt-0.5 font-medium">
        {value || '\u00a0'}
      </div>
    </div>
  );
}

function SignatureField({ label, name }: { label: string; name?: string }) {
  return (
    <div className="text-center">
      <div className="h-12 border-b border-slate-500" />
      <div className="pt-1.5 font-semibold">{label}</div>
      <div className="mt-0.5 min-h-[14px] text-[10px] text-slate-600">
        {name ? `ชื่อ ${name}` : 'ชื่อ ____________________'}
      </div>
      <div className="mt-1 text-[10px]">
        วันที่ ______ / ______ / ______ เวลา ______ : ______ น.
      </div>
    </div>
  );
}

function TransferDocument({
  transfer,
  fromBranchName,
  toBranchName,
  products,
  form,
}: {
  transfer: TransferView;
  fromBranchName: string;
  toBranchName: string;
  products: Product[];
  form: TransferPrintFormState;
}) {
  const productById = new Map(products.map((product) => [product.id, product]));
  const totalQty = transfer.lines.reduce((total, line) => total + line.qty, 0);

  return (
    <article
      aria-label="ตัวอย่างใบโอนสินค้า"
      className="a4-print-root transfer-print-root box-border flex min-h-[297mm] w-[210mm] flex-col bg-white p-[12mm] text-[11px] leading-relaxed text-slate-950 shadow-lg ring-1 ring-slate-200"
    >
      <header className="grid grid-cols-[1fr_auto] items-start gap-8 border-b-2 border-slate-900 pb-3">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.12em]">
            PANGSUAY STOCK TRANSFER
          </div>
          <h2 className="mt-0.5 text-[22px] font-bold">ใบโอนสินค้า</h2>
          <div className="text-[10px] tracking-wide">
            เอกสารนำส่งสินค้าระหว่างสาขา
          </div>
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5 text-right">
          <dt className="text-slate-500">เลขที่</dt>
          <dd className="font-bold tabular-nums">{transfer.referenceNo}</dd>
          <dt className="text-slate-500">วันที่สร้าง</dt>
          <dd className="tabular-nums">{fmtDateTime(transfer.createdAt)}</dd>
          <dt className="text-slate-500">สถานะ</dt>
          <dd
            className={
              transfer.status === 'cancelled'
                ? 'font-bold text-red-700'
                : 'font-bold'
            }
          >
            {STATUS_TH[transfer.status]}
          </dd>
          <dt className="text-slate-500">จัดทำโดย</dt>
          <dd>{transfer.createdByName ?? 'ไม่ทราบชื่อ'}</dd>
          {transfer.receivedAt && (
            <>
              <dt className="text-slate-500">รับเข้าเมื่อ</dt>
              <dd className="tabular-nums">
                {fmtDateTime(transfer.receivedAt)}
              </dd>
            </>
          )}
        </dl>
      </header>

      {transfer.status === 'cancelled' && (
        <div className="mt-3 border-2 border-red-700 py-1.5 text-center text-[14px] font-bold tracking-[0.18em] text-red-700">
          ยกเลิกแล้ว / CANCELLED
        </div>
      )}

      <section className="mt-4 grid grid-cols-[1fr_24px_1fr] items-stretch gap-3">
        <div className="border border-slate-400 p-3">
          <div className="text-[9px] font-bold text-slate-500">
            รับสินค้าจาก / From
          </div>
          <div className="mt-1 text-[15px] font-bold">{fromBranchName}</div>
          <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 text-[10px]">
            <span className="text-slate-500">ผู้ประสานงาน</span>
            <span className="font-medium">{form.fromContact || '-'}</span>
            <span className="text-slate-500">โทรศัพท์</span>
            <span className="font-medium">{form.fromPhone || '-'}</span>
          </div>
        </div>
        <div
          aria-hidden="true"
          className="flex items-center justify-center text-[20px] font-bold"
        >
          →
        </div>
        <div className="border border-slate-400 p-3">
          <div className="text-[9px] font-bold text-slate-500">
            นำส่งที่ / To
          </div>
          <div className="mt-1 text-[15px] font-bold">{toBranchName}</div>
          <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 text-[10px]">
            <span className="text-slate-500">ผู้ประสานงาน</span>
            <span className="font-medium">{form.toContact || '-'}</span>
            <span className="text-slate-500">โทรศัพท์</span>
            <span className="font-medium">{form.toPhone || '-'}</span>
          </div>
        </div>
      </section>

      <section className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 border border-slate-300 p-3">
        <DocumentField label="ผู้ขนส่ง / ผู้รับของ" value={form.courierName} />
        <DocumentField label="เบอร์ติดต่อ" value={form.courierPhone} />
        <DocumentField
          label="วันและเวลานัดรับ"
          value={[
            printDate(form.pickupDate),
            form.pickupTime ? `เวลา ${form.pickupTime} น.` : '',
          ]
            .filter(Boolean)
            .join(' ')}
        />
        <DocumentField
          label="ทะเบียนรถ / ช่องทางขนส่ง"
          value={form.vehicleOrChannel}
        />
      </section>

      <table className="mt-4 w-full table-fixed border-collapse">
        <thead>
          <tr className="border-y-2 border-slate-900 text-left">
            <th className="w-9 py-1.5 text-center">ลำดับ</th>
            <th className="py-2">สินค้า</th>
            <th className="w-28 py-1.5">บาร์โค้ด</th>
            <th className="w-14 py-1.5 text-right">จำนวน</th>
            <th className="w-16 py-1.5">หน่วย</th>
            <th className="w-14 py-1.5 text-center">ตรวจแล้ว</th>
          </tr>
        </thead>
        <tbody>
          {transfer.lines.map((line, index) => {
            const product = productById.get(line.productId);
            const baseUnit =
              product?.units.find((unit) => unit.factor === 1) ??
              product?.units[0];
            return (
              <tr
                key={`${line.productId}-${index}`}
                className="border-b border-slate-300"
              >
                <td className="py-2 text-center tabular-nums">{index + 1}</td>
                <td className="py-2 pr-2 font-semibold">
                  {product?.name ?? line.productId}
                </td>
                <td className="py-2 tabular-nums">
                  {baseUnit?.barcode ?? '-'}
                </td>
                <td className="py-2 text-right font-bold tabular-nums">
                  {fmtInt(line.qty)}
                </td>
                <td className="py-2">
                  {product?.baseUnit ?? baseUnit?.unit ?? 'หน่วย'}
                </td>
                <td className="py-2 text-center">
                  <span className="inline-block h-4 w-4 border border-slate-700" />
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-b-2 border-slate-900 font-bold">
            <td className="py-2" colSpan={3}>
              รวม {fmtInt(transfer.lines.length)} รายการ
            </td>
            <td className="py-2 text-right tabular-nums">{fmtInt(totalQty)}</td>
            <td className="py-2" colSpan={2}>
              หน่วยฐาน
            </td>
          </tr>
        </tfoot>
      </table>

      <section className="mt-4">
        <div className="text-[9px] font-semibold text-slate-500">หมายเหตุ</div>
        <div className="mt-1 min-h-10 whitespace-pre-line border-b border-slate-400">
          {form.note || '\u00a0'}
        </div>
      </section>

      <div className="transfer-print-signoff mt-auto pt-8">
        <footer className="grid grid-cols-3 gap-8">
          <SignatureField
            label="ผู้จัดสินค้า / ต้นทาง"
            name={form.fromContact}
          />
          <SignatureField
            label="ผู้ขนส่ง / ผู้รับของ"
            name={form.courierName}
          />
          <SignatureField
            label="ผู้รับสินค้า / ปลายทาง"
            name={form.toContact}
          />
        </footer>

        <div className="mt-6 border-t border-slate-300 pt-2 text-center text-[9px] text-slate-500">
          เอกสารนี้ใช้ประกอบการเคลื่อนย้ายสินค้าระหว่างสาขา
          กรุณาตรวจนับสินค้าและลงลายมือชื่อทุกครั้ง
        </div>
      </div>
    </article>
  );
}

function TransferPrintEditor({
  transfer,
  fromBranchName,
  toBranchName,
  products,
  onClose,
}: {
  transfer: TransferView;
  fromBranchName: string;
  toBranchName: string;
  products: Product[];
  onClose: () => void;
}) {
  const [form, setForm] = useState(() => createInitialForm(transfer));
  const set = <K extends keyof TransferPrintFormState>(
    key: K,
    value: TransferPrintFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const onPrint = (event: FormEvent) => {
    event.preventDefault();
    document.body.classList.add('transfer-printing');
    try {
      window.print();
    } finally {
      document.body.classList.remove('transfer-printing');
    }
  };

  return (
    <form onSubmit={onPrint}>
      <div className="transfer-print-screen-only grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="transfer-print-controls min-w-0 space-y-5 lg:max-h-[calc(82dvh-112px)] lg:overflow-y-auto lg:pr-2">
          <section aria-labelledby="transfer-contact-heading">
            <div>
              <h3 id="transfer-contact-heading" className="font-bold text-ink">
                ผู้ประสานงานแต่ละสาขา
              </h3>
              <p className="mt-0.5 text-sm-label text-ink-3">
                ระบบใส่ชื่อผู้สร้างและผู้รับล่าสุดให้ แก้ไขได้ก่อนพิมพ์
              </p>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="ผู้ประสานงานต้นทาง">
                <Input
                  aria-label="ผู้ประสานงานต้นทาง"
                  value={form.fromContact}
                  onChange={(event) => set('fromContact', event.target.value)}
                />
              </Field>
              <Field label="โทรศัพท์ต้นทาง">
                <Input
                  aria-label="โทรศัพท์ต้นทาง"
                  inputMode="tel"
                  value={form.fromPhone}
                  onChange={(event) => set('fromPhone', event.target.value)}
                />
              </Field>
              <Field label="ผู้ประสานงานปลายทาง">
                <Input
                  aria-label="ผู้ประสานงานปลายทาง"
                  value={form.toContact}
                  onChange={(event) => set('toContact', event.target.value)}
                />
              </Field>
              <Field label="โทรศัพท์ปลายทาง">
                <Input
                  aria-label="โทรศัพท์ปลายทาง"
                  inputMode="tel"
                  value={form.toPhone}
                  onChange={(event) => set('toPhone', event.target.value)}
                />
              </Field>
            </div>
          </section>

          <section
            aria-labelledby="transfer-delivery-heading"
            className="border-t border-line pt-5"
          >
            <div>
              <h3 id="transfer-delivery-heading" className="font-bold text-ink">
                ข้อมูลการขนส่ง
              </h3>
              <p className="mt-0.5 text-sm-label text-ink-3">
                เว้นว่างได้ หากต้องการกรอกด้วยลายมือหลังพิมพ์
              </p>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="ผู้ขนส่ง / ผู้รับของ">
                <Input
                  aria-label="ผู้ขนส่ง / ผู้รับของ"
                  value={form.courierName}
                  onChange={(event) => set('courierName', event.target.value)}
                />
              </Field>
              <Field label="เบอร์ติดต่อ">
                <Input
                  aria-label="เบอร์ติดต่อผู้ขนส่ง"
                  inputMode="tel"
                  value={form.courierPhone}
                  onChange={(event) => set('courierPhone', event.target.value)}
                />
              </Field>
              <Field label="วันนัดรับ">
                <Input
                  aria-label="วันนัดรับ"
                  type="date"
                  value={form.pickupDate}
                  onChange={(event) => set('pickupDate', event.target.value)}
                />
              </Field>
              <Field label="เวลานัดรับ">
                <Input
                  aria-label="เวลานัดรับ"
                  type="time"
                  value={form.pickupTime}
                  onChange={(event) => set('pickupTime', event.target.value)}
                />
              </Field>
              <Field className="sm:col-span-2" label="ทะเบียนรถ / ช่องทางขนส่ง">
                <Input
                  aria-label="ทะเบียนรถ / ช่องทางขนส่ง"
                  value={form.vehicleOrChannel}
                  onChange={(event) =>
                    set('vehicleOrChannel', event.target.value)
                  }
                />
              </Field>
              <Field
                className="sm:col-span-2"
                label="หมายเหตุ"
                hint="ไม่บังคับ"
              >
                <textarea
                  aria-label="หมายเหตุใบโอน"
                  className={TEXTAREA_CLASS}
                  value={form.note}
                  onChange={(event) => set('note', event.target.value)}
                />
              </Field>
            </div>
          </section>

          <div className="sticky bottom-0 flex justify-end gap-2 border-t border-line bg-surface/95 py-3 backdrop-blur-sm">
            <Btn type="button" onClick={onClose}>
              ปิด
            </Btn>
            <Btn kind="primary" type="submit">
              พิมพ์เอกสาร
            </Btn>
          </div>
        </div>

        <aside
          aria-label="ตัวอย่างเอกสาร"
          className="transfer-print-preview-panel min-w-0 rounded-card border border-line bg-bg-warm/70 p-3 lg:sticky lg:top-0"
        >
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h3 className="text-body font-bold text-ink">ตัวอย่างใบโอน</h3>
              <p className="text-sm-label text-ink-3">กระดาษ A4 แนวตั้ง</p>
            </div>
            <span className="font-mono text-sm-label text-ink-3">
              #{transfer.referenceNo}
            </span>
          </div>
          <div className="transfer-print-preview-frame flex justify-center overflow-hidden rounded-btn bg-slate-200/70 p-2">
            <TransferDocument
              transfer={transfer}
              fromBranchName={fromBranchName}
              toBranchName={toBranchName}
              products={products}
              form={form}
            />
          </div>
        </aside>
      </div>
    </form>
  );
}

export function TransferPrintModal({
  open,
  transfer,
  fromBranchName,
  toBranchName,
  products,
  onClose,
}: {
  open: boolean;
  transfer: TransferView;
  fromBranchName: string;
  toBranchName: string;
  products: Product[];
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      title={
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span>พิมพ์ใบโอน</span>
          <span className="font-mono text-sm-label font-semibold text-ink-3">
            #{transfer.referenceNo}
          </span>
        </span>
      }
      description="ตรวจข้อมูลผู้ส่ง ผู้รับ และรูปแบบเอกสารก่อนสั่งพิมพ์"
      onClose={onClose}
      className="transfer-print-dialog !w-[min(1040px,94vw)] !max-h-[82dvh]"
    >
      <TransferPrintEditor
        key={transfer.id}
        transfer={transfer}
        fromBranchName={fromBranchName}
        toBranchName={toBranchName}
        products={products}
        onClose={onClose}
      />
    </Modal>
  );
}
